import React, { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Box, Hidden } from 'native-base';
import { logUserEvent } from 'fmc-digital-common';
import { Helpers, useTrackingItemsQuery, selectDisclosures, useESignDetailsQuery } from '../../../hooks';
import { prepareTaskData } from '../../../utils';
import { TaskTable, TaskLoader } from '../../../../components';
import { useLipStore } from '../../../reducers';
import DocUploadModal from '../DocUploadModal';
import Functions from '../../../../utils/Functions';
import { ESignContext } from '../../../../loaninprogress/hooks/Esign';
import { has } from 'lodash';
export const TaskSection = ({
  loanNumber,
  authToken,
  widgets,
  onBrowseFiles,
  onTakePhoto,
  isEsignUpdating,
  setEsignUpdating,
  handleOnTokenResponse,
  activeDisclosedUserName,
  onHandleMobileScroll,
  selfServUrl,
  xApiKey,
  onSelectPhoto,
  onPreview
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [refetchInterval, setRefetchInterval] = useState(false);
  const [invalidateCache, setInvalidateCache] = useState(false);
  const [taskData, setTaskData] = useState([]);
  const [esignStatusDetails, setEsignStatusDetails] = useState({});
  const [refreshCount, setRefreshCount] = useState(0);
  const client = useQueryClient();
  const {
    taskName,
    setSelectedFiles,
    isShowDocuploadModal,
    setErrorMessages,
    clearErrorMessages,
    errorMessages,
    clearToastMessageProps,
    setEsignToastMessage
  } = useLipStore(state => state);
  const isTrackingItemEnabled = widgets.indexOf('doc_upload_widget') >= 0 || false;
  const isEsignEnabled = widgets.indexOf('disclosure_widget') >= 0 || false;
  const {
    data: trackingData,
    isLoading: isTrackingItemLoading,
    isError: isTrackingItemError,
    isFetching
  } = useTrackingItemsQuery({
    accessToken: authToken,
    selfServUrl,
    xApiKey,
    loanNumber,
    select: response => Helpers.transformToSections(response),
    enabled: isTrackingItemEnabled
  });
  const {
    isLoading: isEsignLoading,
    data: disclosuresAPIResponse,
    isError: isEsignError,
    isFetching: isEsignFetching
  } = useESignDetailsQuery({
    url: selfServUrl,
    loanNumber,
    select: response => selectDisclosures(response),
    authToken,
    refetchInterval,
    setRefetchInterval,
    enabled: isEsignEnabled,
    loanStatus: 1,
    xApiKey,
    clearToastMessageProps,
    setEsignToastMessage,
    setRefreshCount,
    refreshCount
  });
  const invalidateCacheOnSuccess = useCallback(async () => {
    await client.invalidateQueries({
      queryKey: ['tracking-items', loanNumber]
    });
    setInvalidateCache(false);
  }, [client, loanNumber]);
  useEffect(() => {
    if (disclosuresAPIResponse && has(disclosuresAPIResponse, ['documentsReady'])) {
      if (disclosuresAPIResponse?.documentsReady)
        // if documentReady true no functionality it will load as it is
        {
          setIsLoading(false);
          setEsignStatusDetails(prev => ({
            ...prev,
            infoData: false,
            errorData: false,
            isEnabledEsign: true,
            esignStatusMessage: ""
          }));
        } else {
        //if documentReady false it will perform below functionality
        if (refreshCount < 3) {
          setEsignStatusDetails(prev => ({
            ...prev,
            infoData: true,
            errorData: false,
            isEnabledEsign: false,
            esignStatusMessage: "We are generating your electronic disclosures. 1:30 remaining."
          }));
        } else if (refreshCount >= 3 && refreshCount < 6) {
          setEsignStatusDetails(prev => ({
            ...prev,
            infoData: true,
            errorData: false,
            isEnabledEsign: false,
            esignStatusMessage: "We are generating your electronic disclosures. We apologize, this is taking longer than expected."
          }));
        } else if (refreshCount >= 6) {
          setEsignStatusDetails(prev => ({
            ...prev,
            infoData: false,
            errorData: true,
            isEnabledEsign: false,
            esignStatusMessage: "We ran into an issue generating your electronic disclosures. Please contact your Loan Advisor to regenerate your disclosures."
          }));
        }
      }
    }
  }, [disclosuresAPIResponse, isEsignFetching, refreshCount, invalidateCache]);
  useEffect(() => {
    if (invalidateCache) {
      invalidateCacheOnSuccess();
    }
  }, [invalidateCache, invalidateCacheOnSuccess]);
  useEffect(() => {
    if (!(isEsignEnabled && isEsignLoading) && !(isTrackingItemEnabled && isTrackingItemLoading)) {
      setIsLoading(false);
    }
  }, [isEsignLoading, isTrackingItemLoading, isEsignEnabled, isTrackingItemEnabled]);
  useEffect(() => {
    if (refetchInterval) {
      setTimeout(() => {
        setRefetchInterval(false);
        setEsignUpdating(false);
      }, 30 * 1000);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refetchInterval]);
  useEffect(() => {
    if (isShowDocuploadModal && onHandleMobileScroll) {
      onHandleMobileScroll();
    }
  }, [isShowDocuploadModal, onHandleMobileScroll]);
  const manageUploadFiles = useCallback(files => {
    if (errorMessages && errorMessages.length) clearErrorMessages('NoFile');
    const {
      fileInfo,
      errors
    } = Functions.validateFiles(files);
    setSelectedFiles(fileInfo);
    setErrorMessages(errors);
  }, [errorMessages, clearErrorMessages, setSelectedFiles, setErrorMessages]);
  const onHandleOnBrowse = async () => {
    try {
      if (onBrowseFiles) {
        let files = await onBrowseFiles();
        if (files && files.length) {
          manageUploadFiles(files);
        }
      }
    } catch (e) {}
  };
  const onHandleTakePhoto = async () => {
    logUserEvent({
      action: 'TakeAPhotoClicked',
      type: 'LIPDocUploadPopUp',
      description: 'Task Section Doc Upload Task ' + taskName
    });
    try {
      if (onTakePhoto) {
        let fileDetails = await onTakePhoto();
        if (fileDetails) {
          manageUploadFiles([fileDetails]);
        }
      }
    } catch (e) {}
  };
  const onHandleOnSelectPhoto = async () => {
    logUserEvent({
      action: 'PhotoLibraryClicked',
      type: 'LIPDocUploadPopUp',
      description: 'Task Section Doc Upload Task ' + taskName
    });
    try {
      if (onSelectPhoto) {
        let files = await onSelectPhoto();
        if (files && files.length) {
          manageUploadFiles(files);
        }
      }
    } catch (e) {}
  };
  useEffect(() => {
    if (!isLoading) {
      let newData = prepareTaskData(trackingData, disclosuresAPIResponse);
      setTaskData(newData);
    }
  }, [isLoading, trackingData, disclosuresAPIResponse, isFetching, isEsignFetching]);
  if (!isLoading && isTrackingItemError && isEsignError) return null;
  return /*#__PURE__*/React.createElement(ESignContext.Provider, {
    //@ts-ignore
    value: {
      setRefetchInterval: setRefetchInterval,
      loanNumber: loanNumber,
      url: selfServUrl,
      authToken: authToken,
      handleOnTokenResponse: handleOnTokenResponse,
      activeDisclosedUserName: activeDisclosedUserName,
      isEsignUpdating: isEsignUpdating,
      setEsignUpdating: setEsignUpdating,
      refetchInterval: refetchInterval,
      xApiKey: xApiKey,
      esignStatusDetails: esignStatusDetails
    }
  }, /*#__PURE__*/React.createElement(Box, {
    borderColor: "lightBlue.400",
    borderRadius: 4,
    borderWidth: 1
  }, taskData.length === 0 ? /*#__PURE__*/React.createElement(TaskLoader, null) : /*#__PURE__*/React.createElement(TaskTable, {
    tableData: taskData
  }), /*#__PURE__*/React.createElement(Hidden, {
    platform: ['web']
  }, /*#__PURE__*/React.createElement(DocUploadModal, {
    onTakeAPhoto: onHandleTakePhoto,
    onBrowseFiles: onHandleOnBrowse,
    onSelectPhoto: onHandleOnSelectPhoto,
    onPreview: onPreview
  }))));
};
//# sourceMappingURL=index.js.map